import React, {useState} from 'react';
import Avatar from '@cloudscape-design/chat-components/avatar';
import ChatBubble from '@cloudscape-design/chat-components/chat-bubble';
import {Box, ExpandableSection, Popover, StatusIndicator} from '@cloudscape-design/components';
import type {StatusIndicatorProps} from '@cloudscape-design/components';
import {isToolUIPart, getToolName} from 'ai';
import type {UIMessage, ToolUIPart} from 'ai';
import {Streamdown} from 'streamdown';
import './cassandra.css';

function formatToolDisplayName(tool: ToolUIPart): string {
    const raw = getToolName(tool);
    return raw
        .replace(/^tool-/, '')
        .replace(/Tool$/, '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (c) => c.toUpperCase());
}

function getStatusType(state: ToolUIPart['state']): StatusIndicatorProps.Type {
    switch (state) {
        case 'input-streaming':
        case 'input-available':
            return 'loading';
        case 'output-available':
            return 'success';
        case 'output-error':
        case 'output-denied':
            return 'error';
        case 'approval-requested':
            return 'pending';
        case 'approval-responded':
            return 'loading';
        default:
            return 'info';
    }
}

function getStatusText(tool: ToolUIPart): string {
    const name = formatToolDisplayName(tool);
    switch (tool.state) {
        case 'output-error':
            return `${name} failed`;
        case 'output-denied':
            return `${name} rejected`;
        case 'approval-requested':
            return `${name} needs approval`;
        default:
            return name;
    }
}

function ToolDetail({tool}: {tool: ToolUIPart}) {
    const input = tool.input as Record<string, unknown> | undefined;
    const output = (tool.state === 'output-available' ? tool.output : undefined) as string | Record<string, unknown> | undefined;
    const error = tool.state === 'output-error' ? tool.errorText : undefined;

    return (
        <>
            {input && (
                <div>
                    <Box variant="awsui-key-label">Input</Box>
                    <pre style={{whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto', margin: 0, fontSize: 12}}>
                        {typeof input === 'string' ? input : JSON.stringify(input, null, 2)}
                    </pre>
                </div>
            )}
            {output && (
                <div>
                    <Box variant="awsui-key-label">Output</Box>
                    <pre style={{whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto', margin: 0, fontSize: 12}}>
                        {typeof output === 'string' ? output : JSON.stringify(output, null, 2)}
                    </pre>
                </div>
            )}
            {error && (
                <div>
                    <Box variant="awsui-key-label">Error</Box>
                    <StatusIndicator type="error">{error}</StatusIndicator>
                </div>
            )}
        </>
    );
}

function ToolStep({tool, childTools}: {tool: ToolUIPart; childTools: ToolUIPart[]}) {
    const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
    const toolInProgress = tool.state === 'input-streaming' || tool.state === 'input-available';
    const allSucceeded = tool.state === 'output-available' && childTools.every((c) => c.state === 'output-available');
    const expanded = userExpanded ?? !allSucceeded;

    return (
        <div>
            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <StatusIndicator type={getStatusType(tool.state)}>
                    <Popover fixedWidth size="large" content={<ToolDetail tool={tool} />}>
                        {getStatusText(tool)}
                    </Popover>
                </StatusIndicator>
                {!toolInProgress && childTools.length > 0 && (
                    <span
                        role="button"
                        tabIndex={0}
                        style={{cursor: 'pointer', fontSize: 12, color: '#0972d3'}}
                        onClick={() => setUserExpanded(!expanded)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') setUserExpanded(!expanded);
                        }}
                    >
                        {expanded ? 'Hide steps' : `Show ${childTools.length} steps`}
                    </span>
                )}
            </div>
            {(toolInProgress || expanded) && childTools.length > 0 && (
                <div style={{marginLeft: 16, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2}}>
                    {childTools.map((child) => (
                        <StatusIndicator key={child.toolCallId} type={getStatusType(child.state)}>
                            <Popover fixedWidth size="large" content={<ToolDetail tool={child} />}>
                                {getStatusText(child)}
                            </Popover>
                        </StatusIndicator>
                    ))}
                </div>
            )}
        </div>
    );
}

interface ChatMessageProps {
    message: UIMessage;
    isStreaming: boolean;
    userAlias?: string;
}

export default function ChatMessage({message, isStreaming, userAlias}: ChatMessageProps) {
    const isUser = message.role === 'user';

    const toolParentMap = new Map<string, string>();
    for (const part of message.parts) {
        if ((part as any).type === 'data-tool-parent') {
            const {toolCallId, parentToolCallId} = (part as any).data;
            toolParentMap.set(toolCallId, parentToolCallId);
        }
    }

    const getToolParent = (tool: ToolUIPart): string | undefined => {
        if (tool.state !== 'input-streaming') {
            const meta = (tool as any).callProviderMetadata?.frida?.parentToolCallId;
            if (typeof meta === 'string') return meta;
        }
        return toolParentMap.get(tool.toolCallId);
    };

    const avatar = isUser ? (
        <Avatar ariaLabel="User" tooltipText="User" initials={userAlias?.slice(0, 2).toUpperCase()} />
    ) : (
        <Avatar
            color="gen-ai"
            iconName="gen-ai"
            ariaLabel="Assistant"
            tooltipText="Cassandra"
            loading={isStreaming && !message.parts.some((p) => p.type === 'text' && (p as any).text)}
        />
    );

    if (isUser) {
        const text = message.parts
            .filter((p) => p.type === 'text')
            .map((p) => (p as any).text ?? '')
            .join('');
        return (
            <ChatBubble ariaLabel="User message" type="outgoing" avatar={avatar}>
                <div className="cassandra-streamdown-scope">
                    <Streamdown>{text}</Streamdown>
                </div>
            </ChatBubble>
        );
    }

    const content: React.ReactNode[] = [];

    message.parts.forEach((part, index) => {
        if (part.type === 'text') {
            const text = (part as any).text ?? '';
            if (text) {
                content.push(
                    <div key={`text-${index}`} className="cassandra-streamdown-scope">
                        <Streamdown animated isAnimating={isStreaming}>
                            {text}
                        </Streamdown>
                    </div>
                );
            }
        } else if (part.type === 'reasoning') {
            content.push(
                <ExpandableSection key={`reasoning-${index}`} headerText="Reasoning" variant="footer" defaultExpanded={false}>
                    <Box variant="small">{(part as any).text ?? (part as any).reasoning ?? ''}</Box>
                </ExpandableSection>
            );
        } else if (isToolUIPart(part)) {
            const tool = part as ToolUIPart;
            const parent = getToolParent(tool);
            if (!parent) {
                const children = message.parts.filter(
                    (p) => isToolUIPart(p) && getToolParent(p as ToolUIPart) === tool.toolCallId
                ) as ToolUIPart[];
                content.push(<ToolStep key={tool.toolCallId} tool={tool} childTools={children} />);
            }
        }
    });

    const hasContent = content.length > 0;

    return (
        <ChatBubble ariaLabel="Assistant message" type="incoming" avatar={avatar} showLoadingBar={isStreaming && !hasContent}>
            <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                {hasContent ? content : isStreaming ? <StatusIndicator type="loading">Thinking...</StatusIndicator> : null}
            </div>
        </ChatBubble>
    );
}
